import { useState, useMemo, useId, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getArticles,
  getBookings,
  getPartners,
  getTrips,
  updateBookingDate,
  updateBookingStatus,
} from "../services/api";
import type { Booking, BookingArticle, Article, Partner } from "@common/types";
import DeparturePlaceSelect from "./DeparturePlaceSelect";
import BookingDialog from "./BookingDialog";
import { useLocaleStore } from "../stores/locale-store";
import { localizeTrip } from "./trip/trip-content";
import { useTranslation } from "react-i18next";
import { ApiError } from "../services/api-error";
import { bookingSaveError } from "../utils/booking-errors";
import { vesselConflict, slotLabel } from "../utils/slot-availability";
import { allowedSlotsForTrip } from "@common/booking-overlap";
import { changesWhatGuestSees } from "../utils/booking-edit";
import { hasTripStartPassed } from "../utils/booking";

interface Props {
  booking: Booking;
  isAdmin: boolean;
  onClose: () => void;
}

/**
 * Coerce/drop malformed article entries before they ever reach state.
 * Older bookings can carry articles saved before the backend's strict
 * validation existed (or synced from Fortnox when a numeric field arrived
 * as a string) — without this, simply opening Edit and saving *any*
 * unrelated change (e.g. guest count) resends the stale bad entry and the
 * save is rejected with "Each article requires ...".
 */
export function sanitizeArticles(articles: BookingArticle[] | undefined): BookingArticle[] {
  if (!articles) return [];
  return articles
    .filter(
      (a) =>
        typeof a.articleNumber === "string" &&
        a.articleNumber.length > 0 &&
        typeof a.description === "string" &&
        a.description.length > 0,
    )
    .map((a) => {
      const quantity = Number(a.quantity);
      const unitPrice = Number(a.unitPrice);
      return {
        ...a,
        quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
        unitPrice: Number.isFinite(unitPrice) && unitPrice >= 0 ? unitPrice : 0,
      };
    });
}

export default function EditBookingModal({ booking, isAdmin, onClose }: Props) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const language = useLocaleStore(state => state.language);
  const titleId = useId();
  const notificationTitleId = useId();

  const [editDate, setEditDate] = useState(booking.date);
  const [editTimeSlot, setEditTimeSlot] = useState<string>(booking.timeSlot || "10:00");
  const [editUseCustomTime, setEditUseCustomTime] = useState(!!booking.customStartTime);
  const [editCustomTime, setEditCustomTime] = useState(booking.customStartTime || "");
  const [editCustomDurationHours, setEditCustomDurationHours] = useState(
    String(booking.customDurationHours ?? (booking.tripDurationMinutes ? booking.tripDurationMinutes / 60 : 3)),
  );
  const [editNotes, setEditNotes] = useState(booking.notes || "");
  const [editTripPrice, setEditTripPrice] = useState(booking.tripPrice?.toString() || "");
  const [editExtraCost, setEditExtraCost] = useState(booking.extraCost?.toString() || "");
  const [editExtraCostDescription, setEditExtraCostDescription] = useState(booking.extraCostDescription || "");
  const [editNumberOfGuests, setEditNumberOfGuests] = useState(booking.numberOfGuests || 1);
  const [editArticles, setEditArticles] = useState<BookingArticle[]>(
    sanitizeArticles(booking.articles),
  );
  const [editPartnerId, setEditPartnerId] = useState(booking.partnerId || "");
  const [editPartnerSearchTerm, setEditPartnerSearchTerm] = useState("");
  const [showEditPartnerDropdown, setShowEditPartnerDropdown] = useState(false);
  const [editBookingReference, setEditBookingReference] = useState(booking.bookingReference || "");
  const [editGuestName, setEditGuestName] = useState(booking.customer?.name || "");
  const [editGuestCountry, setEditGuestCountry] = useState(booking.customer?.country || "");
  const [editGuestMobile, setEditGuestMobile] = useState(booking.customer?.mobile || "");
  const [editGuestEmail, setEditGuestEmail] = useState(booking.customer?.email || "");
  const [editTripId, setEditTripId] = useState(booking.tripId);
  const [editDepartureId, setEditDepartureId] = useState(booking.departure?.locationId || "");
  const [editUpdatePrice, setEditUpdatePrice] = useState(true);
  const [pendingPayload, setPendingPayload] = useState<any | null>(null);

  // Mark complete without a Fortnox invoice — trip invoiced outside SeaSlot,
  // or no invoice needed at all
  const [completeInvoiceNumber, setCompleteInvoiceNumber] = useState("");
  const [completeInvoiceTotal, setCompleteInvoiceTotal] = useState("");
  const canMarkComplete =
    isAdmin && booking.status === "confirmed" && hasTripStartPassed(booking);

  const { data: articlesData } = useQuery({
    queryKey: ["articles"],
    queryFn: getArticles,
    enabled: isAdmin,
  });

  const { data: partnersData, isLoading: isPartnersLoading } = useQuery({
    queryKey: ["partners"],
    queryFn: getPartners,
    enabled: isAdmin,
  });

  const { data: tripsData } = useQuery({
    queryKey: ["trips"],
    queryFn: getTrips,
    enabled: isAdmin,
  });

  const partners: Partner[] = partnersData?.data || [];
  const trips = tripsData?.data || [];

  // The chosen date's bookings, to warn when the vessel is busy (admins only; the server checks everyone)
  const { data: dateBookingsData } = useQuery({
    queryKey: ["bookings-on-date", editDate],
    queryFn: () => getBookings({ startDate: editDate, endDate: editDate }),
    enabled: isAdmin && !!editDate,
  });
  const dateBookings = dateBookingsData?.data || [];
  const editTrip = trips.find((tr) => tr.id === editTripId);
  const unchangedTrip = editTripId === booking.tripId; // then the stored duration snapshot applies
  const editConflict = useMemo(
    () =>
      isAdmin && editTrip
        ? vesselConflict(dateBookings, unchangedTrip && booking.tripDurationMinutes ? { ...editTrip, durationMinutes: booking.tripDurationMinutes } : editTrip, editDate, trips, {
            timeSlot: editTimeSlot,
            ...(editUseCustomTime && editCustomTime
              ? { customStartTime: editCustomTime, customDurationHours: parseFloat(editCustomDurationHours) || undefined }
              : {}),
          }, booking.id)
        : undefined,
    [isAdmin, editTrip, unchangedTrip, booking.tripDurationMinutes, dateBookings, editDate, trips, editTimeSlot, editUseCustomTime, editCustomTime, editCustomDurationHours, booking.id],
  );
  const [editOverride, setEditOverride] = useState(false);
  const offeredSlots: string[] = useMemo(() => {
    const slots: string[] = editTrip ? [...allowedSlotsForTrip(editTrip)] : ["10:00", "14:00"];
    if (booking.timeSlot && !slots.includes(booking.timeSlot)) slots.push(booking.timeSlot); // a legacy value stays selectable
    return slots;
  }, [editTrip, booking.timeSlot]);

  const tripArticleNumbers = useMemo(() => {
    const nums = new Set<string>();
    trips.forEach((t) => {
      if (t.fortnoxArticleNumber) nums.add(t.fortnoxArticleNumber);
    });
    return nums;
  }, [trips]);

  const activeArticles: Article[] = useMemo(
    () =>
      (articlesData?.data || []).filter(
        (a) => a.isEnabled && !tripArticleNumbers.has(a.articleNumber),
      ),
    [articlesData?.data, tripArticleNumbers],
  );

  const filteredPartners = useMemo(() => {
    const active = partners.filter((p) => p.isActive);
    if (!editPartnerSearchTerm.trim()) return active;
    const q = editPartnerSearchTerm.toLowerCase();
    return active.filter(
      (p) =>
        p.name.toLowerCase().includes(q) || p.domain.toLowerCase().includes(q),
    );
  }, [partners, editPartnerSearchTerm]);

  const updateDateMutation = useMutation({
    mutationFn: ({
      id,
      currentDate,
      newDate,
      newTimeSlot,
      customStartTime,
      customDurationHours,
      notes,
      tripPrice,
      extraCost,
      extraCostDescription,
      includeLunch,
      numberOfGuests,
      articles,
      partnerId,
      bookingReference,
      customer,
      suppressEmailNotification,
      tripId,
      departureLocationId,
      expectedUpdatedAt,
      overrideOverlap,
    }: {
      id: string;
      currentDate: string;
      newDate: string;
      newTimeSlot?: string;
      customStartTime?: string;
      customDurationHours?: number;
      notes?: string;
      tripPrice?: number;
      extraCost?: number;
      extraCostDescription?: string;
      includeLunch?: boolean;
      numberOfGuests?: number;
      articles?: BookingArticle[];
      partnerId?: string;
      bookingReference?: string;
      customer?: { name?: string; country?: string; mobile?: string; email?: string };
      suppressEmailNotification?: boolean;
      tripId?: string;
      departureLocationId?: string | null;
      expectedUpdatedAt?: string;
      overrideOverlap?: boolean;
    }) =>
      updateBookingDate(
        id,
        currentDate,
        newDate,
        newTimeSlot,
        customStartTime,
        notes,
        extraCost,
        extraCostDescription,
        includeLunch,
        numberOfGuests,
        customDurationHours,
        articles,
        partnerId,
        bookingReference,
        customer,
        tripPrice,
        suppressEmailNotification,
        tripId,
        departureLocationId,
        expectedUpdatedAt,
        overrideOverlap,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bookings"] });
      queryClient.invalidateQueries({ queryKey: ["bookings-availability"] });
      queryClient.invalidateQueries({ queryKey: ["calendar-availability"] });
      queryClient.invalidateQueries({ queryKey: ["booking-history"] });
      setPendingPayload(null);
      onClose();
    },
    // the error is shown in the dialog itself: no second, native alert on top of it
    onError: () => undefined,
  });

  const completeBookingMutation = useMutation({
    mutationFn: () =>
      updateBookingStatus(
        booking.id,
        booking.date,
        "completed",
        undefined,
        completeInvoiceNumber.trim() || undefined,
        completeInvoiceTotal.trim() ? Number(completeInvoiceTotal) : undefined,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bookings"] });
      queryClient.invalidateQueries({ queryKey: ["bookings-availability"] });
      queryClient.invalidateQueries({ queryKey: ["calendar-availability"] });
      queryClient.invalidateQueries({ queryKey: ["booking-history"] });
      onClose();
    },
  });

  // A conflict the browser did not predict but the server found: the same override applies
  const busyError = updateDateMutation.error;
  const choiceKey = `${editDate}|${editTripId}|${editUseCustomTime ? `${editCustomTime}/${editCustomDurationHours}` : editTimeSlot}`;
  const choiceKeyRef = useRef(choiceKey);
  choiceKeyRef.current = choiceKey;
  const [busyKey, setBusyKey] = useState<string | null>(null);
  useEffect(() => { setBusyKey(busyError ? choiceKeyRef.current : null); }, [busyError]);
  useEffect(() => { setEditOverride(false); }, [choiceKey]);
  const serverConflict = isAdmin && busyKey === choiceKey && busyError instanceof ApiError && busyError.code === "vessel_busy"
    ? (busyError.details as { tripName?: string; from?: string; to?: string } | undefined) ?? {}
    : undefined;
  const shownConflict = editConflict ?? (serverConflict && { tripName: serverConflict.tripName, from: serverConflict.from ?? "", to: serverConflict.to ?? "" });
  useEffect(() => { if (!shownConflict) setEditOverride(false); }, [shownConflict]);
  const saveError = (error: unknown) =>
    error instanceof ApiError && error.code === "booking_changed" ? t("common.bookingChangedByOther") : bookingSaveError(error, t, isAdmin);

  const handleSave = () => {
    const tripPriceNum = editTripPrice ? parseFloat(editTripPrice) : undefined;
    const extraCostNum = editExtraCost ? parseFloat(editExtraCost) : undefined;

    const payload: any = {
      id: booking.id,
      currentDate: booking.date,
      newDate: editDate,
      newTimeSlot: editTimeSlot,
      notes: editNotes || undefined,
      numberOfGuests: editNumberOfGuests,
      expectedUpdatedAt: booking.updatedAt,
    };
    if (isAdmin && shownConflict && editOverride) payload.overrideOverlap = true;

    // Only a changed departure place is sent; choosing the empty option removes it
    if (editDepartureId !== (booking.departure?.locationId || "")) {
      payload.departureLocationId = editDepartureId || null;
    }

    if (isAdmin) {
      payload.customStartTime = editUseCustomTime ? editCustomTime : undefined;
      payload.customDurationHours = editUseCustomTime
        ? parseFloat(editCustomDurationHours) || undefined
        : undefined;
      payload.tripPrice = tripPriceNum;
      payload.extraCost = extraCostNum;
      payload.extraCostDescription = editExtraCostDescription || undefined;
      payload.articles = editArticles;

      if (editPartnerId !== (booking.partnerId || "")) {
        payload.partnerId = editPartnerId;
      }
      if (editBookingReference !== (booking.bookingReference || "")) {
        payload.bookingReference = editBookingReference;
      }

      const customerChanged =
        editGuestName !== (booking.customer?.name || "") ||
        editGuestCountry !== (booking.customer?.country || "") ||
        editGuestMobile !== (booking.customer?.mobile || "") ||
        editGuestEmail !== (booking.customer?.email || "");
      if (customerChanged) {
        payload.customer = {
          name: editGuestName,
          country: editGuestCountry,
          mobile: editGuestMobile,
          email: editGuestEmail || undefined,
        };
      }
    }

    if (isAdmin && editTripId !== booking.tripId) {
      payload.tripId = editTripId;
      if (tripPriceNum !== undefined) {
        payload.tripPrice = tripPriceNum;
      } else if (editUpdatePrice) {
        payload.tripPrice = undefined;
      } else {
        payload.tripPrice = booking.tripPrice;
      }
    }

    if (isAdmin && changesWhatGuestSees(payload, booking)) {
      // the guest will notice this change: ask whether to send an update notice
      setPendingPayload(payload);
    } else if (isAdmin) {
      // a note, price or internal reference: no notice to the guest
      updateDateMutation.mutate({ ...payload, suppressEmailNotification: true });
    } else {
      updateDateMutation.mutate(payload);
    }
  };

  return (
    <>
      {/* Edit modal */}
      <BookingDialog titleId={titleId} onClose={onClose} busy={updateDateMutation.isPending || completeBookingMutation.isPending}>
          <div className="flex justify-between items-start p-6 pb-4 border-b border-gray-100 sticky top-0 bg-white z-10 shrink-0">
            <div>
              <h2 id={titleId} className="text-lg font-semibold text-sea-900">
                {t("partnerPages.edit.title")}
              </h2>
              <p className="text-sm text-gray-500 mt-0.5">
                {t("partnerPages.edit.confirmation")} {booking.confirmationCode}
              </p>
            </div>
            <button
              onClick={onClose}
              disabled={updateDateMutation.isPending}
              className="min-h-11 min-w-11 -mr-2 text-primary-600 hover:text-sea-900 text-xl leading-none"
              aria-label={t("partnerPages.edit.close")}
            >
              ×
            </button>
          </div>

          <div className="min-h-0 p-4 pt-4 sm:p-6 sm:pt-4 overflow-y-auto [overflow-wrap:anywhere]">
          {canMarkComplete && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-3">
              <p className="text-sm text-amber-800">
                {t("partnerPages.edit.manualInvoice")}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="booking-edit-1" className="label text-xs">
                    {t("partnerPages.edit.invoiceReference")}
                  </label>
                  <input id="booking-edit-1"
                    type="text"
                    className="input"
                    value={completeInvoiceNumber}
                    onChange={(e) => setCompleteInvoiceNumber(e.target.value)}
                    placeholder={t("partnerPages.edit.optional")}
                  />
                </div>
                <div>
                  <label htmlFor="booking-edit-2" className="label text-xs">{t("partnerPages.edit.invoiceAmount")}</label>
                  <input id="booking-edit-2"
                    type="number"
                    step="0.01"
                    className="input"
                    value={completeInvoiceTotal}
                    onChange={(e) => setCompleteInvoiceTotal(e.target.value)}
                    placeholder={t("partnerPages.edit.optional")}
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={() => completeBookingMutation.mutate()}
                disabled={completeBookingMutation.isPending}
                className="btn-secondary text-xs"
              >
                {completeBookingMutation.isPending
                  ? t("partnerPages.edit.saving")
                  : t("partnerPages.edit.markComplete")}
              </button>
              {completeBookingMutation.isError && (
                <p className="text-xs text-red-600">
                  {(completeBookingMutation.error as Error).message}
                </p>
              )}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label htmlFor="booking-edit-3" className="label">{t("partnerPages.edit.date")}</label>
              <input id="booking-edit-3"
                type="date"
                className="input"
                value={editDate}
                onChange={(e) => setEditDate(e.target.value)}
              />
            </div>

            <div>
              <label htmlFor="booking-edit-4" className="label">{t("partnerPages.edit.timeSlot")}</label>
              <select id="booking-edit-4"
                className="input"
                value={editTimeSlot}
                onChange={(e) => setEditTimeSlot(e.target.value)}
                disabled={editUseCustomTime}
              >
                {offeredSlots.map((slot) => (
                  <option key={slot} value={slot}>
                    {slot === "10:00" ? t("partnerPages.edit.morning") : slot === "14:00" ? t("partnerPages.edit.afternoon") : slotLabel(slot, t)}
                  </option>
                ))}
              </select>
              {shownConflict && (
                <label className="mt-2 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2 text-sm text-amber-900">
                  <input type="checkbox" className="mt-1" checked={editOverride} onChange={(e) => setEditOverride(e.target.checked)} />
                  <span>
                    <span className="block font-medium">{t("slotScheduling.override")}</span>
                    <span className="block">{t("slotScheduling.conflict", { trip: shownConflict.tripName ?? t("slotScheduling.conflictFallback"), from: shownConflict.from, to: shownConflict.to })}</span>
                  </span>
                </label>
              )}
            </div>

            {/* Number of Guests */}
            <div>
              <label htmlFor="booking-edit-5" className="label">{t("partnerPages.edit.guests")}</label>
              <div className="flex items-center gap-3">
                <button aria-label={t("partnerPages.form.decrease")}
                  type="button"
                  onClick={() =>
                    setEditNumberOfGuests(Math.max(1, editNumberOfGuests - 1))
                  }
                  className="w-11 h-11 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold flex items-center justify-center transition-colors"
                >
                  −
                </button>
                <input id="booking-edit-5"
                  type="number"
                  min="1"
                  max="50"
                  className="input text-center w-20"
                  value={editNumberOfGuests}
                  onChange={(e) => {
                    const value = parseInt(e.target.value) || 1;
                    setEditNumberOfGuests(Math.max(1, Math.min(50, value)));
                  }}
                />
                <button aria-label={t("partnerPages.form.increase")}
                  type="button"
                  onClick={() =>
                    setEditNumberOfGuests(Math.min(50, editNumberOfGuests + 1))
                  }
                  className="w-11 h-11 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold flex items-center justify-center transition-colors"
                >
                  +
                </button>
              </div>
            </div>

            {/* Partner Selection (Admin Only) */}
            {isAdmin && (
              <div className="space-y-2 p-3 bg-purple-50 rounded-lg">
                <label htmlFor="booking-edit-6" className="label">{t("partnerPages.edit.partner")}</label>
                <div className="relative">
                  <input id="booking-edit-6"
                    type="text"
                    className="input pr-24"
                    placeholder={t("partnerPages.edit.partnerPlaceholder")}
                    value={
                      editPartnerId
                        ? partners.find((p) => p.id === editPartnerId)?.name || ""
                        : editPartnerSearchTerm
                    }
                    onChange={(e) => {
                      setEditPartnerSearchTerm(e.target.value);
                      setShowEditPartnerDropdown(true);
                      if (editPartnerId) setEditPartnerId("");
                    }}
                    onFocus={() => setShowEditPartnerDropdown(true)}
                    onBlur={() => {
                      setTimeout(() => setShowEditPartnerDropdown(false), 200);
                    }}
                  />
                  <button
                    type="button"
                    aria-label={t("partnerPages.form.showPartners")}
              aria-expanded={showEditPartnerDropdown}
              className="absolute right-11 top-1/2 flex min-h-11 min-w-11 -translate-y-1/2 items-center justify-center text-primary-600 hover:text-sea-900"
                    onClick={() =>
                      setShowEditPartnerDropdown(!showEditPartnerDropdown)
                    }
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    <svg
                      className={`w-4 h-4 transition-transform ${showEditPartnerDropdown ? "rotate-180" : ""}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  {(editPartnerId || editPartnerSearchTerm) && (
                    <button
                      type="button"
                      aria-label={t("partnerPages.form.clearPartner")}
                className="absolute right-0 top-1/2 flex min-h-11 min-w-11 -translate-y-1/2 items-center justify-center text-primary-600 hover:text-sea-900"
                      onClick={() => {
                        setEditPartnerSearchTerm("");
                        setEditPartnerId("");
                        setShowEditPartnerDropdown(false);
                      }}
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  )}
                  {showEditPartnerDropdown && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
                      <button
                        type="button"
                        className="w-full text-left px-4 py-2 text-sm text-gray-600 hover:bg-gray-100"
                        onClick={() => {
                          setEditPartnerId("");
                          setEditPartnerSearchTerm("");
                          setShowEditPartnerDropdown(false);
                        }}
                      >
                        {t("partnerPages.edit.walkIn")}
                      </button>
                      {isPartnersLoading ? (
                        <div className="px-4 py-2 text-sm text-gray-500">
                          {t("partnerPages.edit.partnersLoading")}
                        </div>
                      ) : filteredPartners.length === 0 ? (
                        <div className="px-4 py-2 text-sm text-gray-500">
                          {t("partnerPages.edit.noPartners")}
                        </div>
                      ) : (
                        filteredPartners.map((partner) => (
                          <button
                            key={partner.id}
                            type="button"
                            className="w-full text-left px-4 py-2 text-sm hover:bg-purple-50"
                            onClick={() => {
                              setEditPartnerId(partner.id);
                              setEditPartnerSearchTerm("");
                              setShowEditPartnerDropdown(false);
                            }}
                          >
                            <span className="font-medium">{partner.name}</span>
                            <span className="text-gray-500 ml-1">
                              ({partner.domain})
                            </span>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
                <p className="text-xs text-purple-700">
                  {editPartnerId
                    ? t("partnerPages.edit.assignedPartner")
                    : t("partnerPages.edit.unassignedPartner")}
                </p>
              </div>
            )}

            {/* Trip Selection (Admin Only) */}
            {isAdmin && (
              <div className="space-y-1">
                <label htmlFor="booking-edit-7" className="block text-sm font-medium text-gray-700">
                  {t("partnerPages.edit.trip")}
                </label>
                <select id="booking-edit-7"
                  value={editTripId}
                  onChange={(e) => {
                    setEditTripId(e.target.value);
                    setEditUpdatePrice(true);
                  }}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sea-500"
                >
                  {trips
                    .filter((t) => t.isActive && !t.isArchived)
                    .map((t) => (
                      <option key={t.id} value={t.id}>
                        {localizeTrip(t, language).name} ({t.durationMinutes / 60}h — {t.price}{" "}
                        {t.currency})
                      </option>
                    ))}
                </select>
                {editTripId !== booking.tripId &&
                  (() => {
                    const newTrip = trips.find((t) => t.id === editTripId);
                    return newTrip ? (
                      <label className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editUpdatePrice}
                          onChange={(e) => setEditUpdatePrice(e.target.checked)}
                          className="rounded border-gray-300"
                        />
                        {t("partnerPages.edit.updatePrice")} {newTrip.price} {newTrip.currency}
                      </label>
                    ) : null;
                  })()}
              </div>
            )}

            {/* Custom Time (Admin Only) */}
            {isAdmin && (
              <div className="space-y-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-sea-600 focus:ring-sea-500"
                    checked={editUseCustomTime}
                    onChange={(e) => {
                      setEditUseCustomTime(e.target.checked);
                      if (!e.target.checked) setEditCustomTime("");
                    }}
                  />
                  <span className="text-sm text-gray-700">
                    {t("partnerPages.edit.customTime")}
                  </span>
                </label>
                {editUseCustomTime && (
                  <div className="space-y-2">
                    <div>
                      <label htmlFor="booking-edit-8" className="text-xs text-amber-700 font-medium">
                        {t("partnerPages.edit.startTime")}
                      </label>
                      <input id="booking-edit-8"
                        type="time"
                        className="input"
                        value={editCustomTime}
                        onChange={(e) => setEditCustomTime(e.target.value)}
                      />
                    </div>
                    <div>
                      <label htmlFor="booking-edit-9" className="text-xs text-amber-700 font-medium">
                        {t("partnerPages.edit.duration")}
                      </label>
                      <input id="booking-edit-9"
                        type="number"
                        min="0.5"
                        max="12"
                        step="0.5"
                        className="input w-24"
                        value={editCustomDurationHours}
                        onChange={(e) =>
                          setEditCustomDurationHours(e.target.value)
                        }
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Booking Reference (Admin Only) */}
            {isAdmin && (
              <div>
                <label htmlFor="booking-edit-10" className="label">{t("partnerPages.edit.reference")}</label>
                <input id="booking-edit-10"
                  type="text"
                  className={`input ${editBookingReference.length > 50 ? "border-red-500 focus:ring-red-500" : ""}`}
                  value={editBookingReference}
                  onChange={(e) => setEditBookingReference(e.target.value)}
                  placeholder={t("partnerPages.edit.referencePlaceholder")}
                  maxLength={60}
                />
                <div className="flex justify-between mt-1">
                  {editBookingReference.length > 50 ? (
                    <p className="text-xs text-red-600 font-medium">
                      {t("partnerPages.edit.referenceLimit")}
                    </p>
                  ) : (
                    <p className="text-xs text-gray-500">
                      {t("partnerPages.edit.referenceHelp")}
                    </p>
                  )}
                  <p className={`text-xs ml-2 shrink-0 ${editBookingReference.length > 50 ? "text-red-600 font-medium" : "text-gray-400"}`}>
                    {editBookingReference.length}/50
                  </p>
                </div>
              </div>
            )}

            {/* Guest Details (Admin Only) */}
            {isAdmin && (
              <div className="space-y-3 p-3 bg-green-50 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700">
                  {t("partnerPages.edit.guestDetails")}
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <label htmlFor="booking-edit-11" className="label text-xs">{t("partnerPages.edit.guestName")}</label>
                    <input id="booking-edit-11"
                      type="text"
                      className="input"
                      value={editGuestName}
                      onChange={(e) => setEditGuestName(e.target.value)}
                      placeholder={t("partnerPages.edit.guestName")}
                    />
                  </div>
                  <div>
                    <label htmlFor="booking-edit-12" className="label text-xs">{t("partnerPages.edit.country")}</label>
                    <input id="booking-edit-12"
                      type="text"
                      className="input"
                      value={editGuestCountry}
                      onChange={(e) => setEditGuestCountry(e.target.value)}
                      placeholder={t("partnerPages.edit.country")}
                    />
                  </div>
                  <div>
                    <label htmlFor="booking-edit-13" className="label text-xs">{t("partnerPages.edit.mobile")}</label>
                    <input id="booking-edit-13"
                      type="text"
                      className="input"
                      value={editGuestMobile}
                      onChange={(e) => setEditGuestMobile(e.target.value)}
                      placeholder={t("partnerPages.edit.mobile")}
                    />
                  </div>
                  <div className="col-span-2">
                    <label htmlFor="booking-edit-14" className="label text-xs">{t("partnerPages.edit.email")}</label>
                    <input id="booking-edit-14"
                      type="email"
                      className="input"
                      value={editGuestEmail}
                      onChange={(e) => setEditGuestEmail(e.target.value)}
                      placeholder={t("partnerPages.edit.emailPlaceholder")}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Departure place */}
            <DeparturePlaceSelect
              id="edit-departure"
              value={editDepartureId}
              onChange={setEditDepartureId}
              emptyLabel={
                booking.departure && !booking.departure.locationId
                  ? `${booking.departure.name}`
                  : t("partnerPages.edit.departureNone")
              }
              current={booking.departure}
            />

            {/* Notes */}
            <div>
              <label htmlFor="booking-edit-15" className="label">{t("partnerPages.edit.notes")}</label>
              <textarea id="booking-edit-15"
                className="input min-h-[80px]"
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                placeholder={t("partnerPages.edit.notesPlaceholder")}
                rows={3}
              />
            </div>

            {/* Price Override (Admin Only) */}
            {isAdmin && (
              <div className="space-y-2 p-3 bg-gray-50 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700">
                  {t("partnerPages.edit.priceOverride")}
                </h4>
                <div>
                  <label htmlFor="booking-edit-16" className="label text-xs">
                    {t("partnerPages.edit.tripPrice")} ({booking.tripCurrency || "SEK"})
                  </label>
                  <input id="booking-edit-16"
                    type="number"
                    className="input"
                    value={editTripPrice}
                    onChange={(e) => setEditTripPrice(e.target.value)}
                    placeholder={booking.tripPrice?.toString() ?? t("partnerPages.edit.defaultPrice")}
                    min="0"
                    step="1"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {t("partnerPages.edit.current")} {booking.tripPrice ?? "—"}{" "}
                    {booking.tripCurrency || ""}
                  </p>
                </div>
              </div>
            )}

            {/* Extra Cost (Admin Only) */}
            {isAdmin && (
              <div className="space-y-3 p-3 bg-gray-50 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700">
                  {t("partnerPages.edit.extraCosts")}
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="booking-edit-17" className="label text-xs">{t("partnerPages.edit.amount")}</label>
                    <input id="booking-edit-17"
                      type="number"
                      className="input"
                      value={editExtraCost}
                      onChange={(e) => setEditExtraCost(e.target.value)}
                      placeholder="0"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div>
                    <label htmlFor="booking-edit-18" className="label text-xs">{t("partnerPages.edit.description")}</label>
                    <input id="booking-edit-18"
                      type="text"
                      className="input"
                      value={editExtraCostDescription}
                      onChange={(e) =>
                        setEditExtraCostDescription(e.target.value)
                      }
                      placeholder={t("partnerPages.edit.extraPlaceholder")}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Additional Articles (Admin Only) */}
            {isAdmin && activeArticles.length > 0 && (
              <div className="space-y-3 p-3 bg-blue-50 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700">
                  {t("partnerPages.edit.articles")}
                </h4>
                <div className="flex gap-2">
                  <select
                    className="input flex-1"
                    id="article-select"
                    defaultValue=""
                    onChange={(e) => {
                      const articleNumber = e.target.value;
                      if (!articleNumber) return;
                      const article = activeArticles.find(
                        (a) => a.articleNumber === articleNumber,
                      );
                      if (!article) return;
                      if (
                        editArticles.some(
                          (a) => a.articleNumber === articleNumber,
                        )
                      ) {
                        e.target.value = "";
                        return;
                      }
                      setEditArticles([
                        ...editArticles,
                        {
                          articleNumber: article.articleNumber,
                          description: article.description,
                          quantity: 1,
                          // Coerce defensively: Fortnox-synced salesPrice can
                          // be a numeric string on records synced before the
                          // article-service.ts fix that normalizes it to number.
                          unitPrice: Number(article.salesPrice),
                          unit: article.unit || "st",
                          vatPercent: article.vatPercent || 25,
                        },
                      ]);
                      e.target.value = "";
                    }}
                  >
                    <option value="">{t("partnerPages.edit.articlePlaceholder")}</option>
                    {activeArticles.map((article) => (
                      <option
                        key={article.articleNumber}
                        value={article.articleNumber}
                        disabled={editArticles.some(
                          (a) => a.articleNumber === article.articleNumber,
                        )}
                      >
                        {article.articleNumber} - {article.description} (
                        {article.salesPrice} SEK)
                      </option>
                    ))}
                  </select>
                </div>

                {editArticles.length > 0 && (
                  <div className="space-y-2">
                    {editArticles.map((article, index) => (
                      <div
                        key={article.articleNumber}
                        className="flex flex-wrap items-center gap-2 p-2 bg-white rounded border"
                      >
                        <div className="flex-1">
                          <div className="text-sm font-medium">
                            {article.description}
                          </div>
                          <div className="text-xs text-gray-500">
                            {article.articleNumber}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => {
                              const updated = [...editArticles];
                              updated[index] = {
                                ...updated[index],
                                quantity: Math.max(
                                  1,
                                  updated[index].quantity - 1,
                                ),
                              };
                              setEditArticles(updated);
                            }}
                            className="w-11 h-11 rounded bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm flex items-center justify-center"
                           aria-label={t("partnerPages.form.decreaseArticle", { name: article.description })}>
                            −
                          </button>
                          <span className="w-8 text-center text-sm">
                            {article.quantity}
                          </span>
                          <button aria-label={t("partnerPages.form.increaseArticle", { name: article.description })}
                            type="button"
                            onClick={() => {
                              const updated = [...editArticles];
                              updated[index] = {
                                ...updated[index],
                                quantity: updated[index].quantity + 1,
                              };
                              setEditArticles(updated);
                            }}
                            className="w-11 h-11 rounded bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm flex items-center justify-center"
                          >
                            +
                          </button>
                        </div>
                        <div className="text-sm text-gray-600 w-20 text-right">
                          {(article.unitPrice * article.quantity).toFixed(0)}{" "}
                          SEK
                        </div>
                        <button aria-label={t("partnerPages.form.removeArticle", { name: article.description })}
                          type="button"
                          onClick={() =>
                            setEditArticles(
                              editArticles.filter((_, i) => i !== index),
                            )
                          }
                          className="min-h-11 min-w-11 text-red-700 hover:text-red-800 p-2"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      </div>
                    ))}
                    <div className="text-sm text-gray-600 text-right pt-2 border-t">
                      {t("partnerPages.edit.total")}{" "}
                      {editArticles
                        .reduce((sum, a) => sum + a.unitPrice * a.quantity, 0)
                        .toFixed(0)}{" "}
                      SEK
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {updateDateMutation.isError && (
            <p role="alert" className="text-sm text-red-700 mt-2">
              {saveError(updateDateMutation.error)}
            </p>
          )}
          </div>

          <div className="flex justify-end gap-3 p-4 sm:p-6 pt-4 border-t border-primary-100 bg-white shrink-0">
            <button
              onClick={onClose}
              className="btn-secondary min-h-11"
              disabled={updateDateMutation.isPending}
            >
              {t("partnerPages.edit.cancel")}
            </button>
            <button
              onClick={handleSave}
              className="btn-primary min-h-11"
              disabled={updateDateMutation.isPending}
            >
              {updateDateMutation.isPending ? t("partnerPages.edit.saving") : t("partnerPages.edit.save")}
            </button>
          </div>
      </BookingDialog>

      {/* Email notification confirmation — admin only */}
      {pendingPayload && (
        <BookingDialog titleId={notificationTitleId} onClose={() => { setPendingPayload(null); updateDateMutation.reset(); }} busy={updateDateMutation.isPending} className="max-w-sm" layer={60}>
          <div className="overflow-y-auto p-5 sm:p-6">
            <h3 id={notificationTitleId} className="text-base font-semibold text-sea-900 mb-2">
              {t("partnerPages.edit.notifyTitle")}
            </h3>
            <p className="text-sm text-gray-600 mb-5">
              {t("partnerPages.edit.notifyMessage")}
            </p>
            {updateDateMutation.isError && (
              <p className="text-xs text-red-600 mb-3">
                {saveError(updateDateMutation.error)}
              </p>
            )}
            {serverConflict && !editConflict && (
              <label className="mb-3 flex items-start gap-2 text-sm text-amber-900">
                <input type="checkbox" className="mt-1" checked={editOverride} onChange={(e) => setEditOverride(e.target.checked)} />
                <span>{t("slotScheduling.override")}</span>
              </label>
            )}
            <div className="flex flex-col gap-2">
              <button
                onClick={() => updateDateMutation.mutate(shownConflict && editOverride ? { ...pendingPayload, overrideOverlap: true } : pendingPayload)}
                disabled={updateDateMutation.isPending}
                className="btn-primary w-full"
              >
                {updateDateMutation.isPending
                  ? t("partnerPages.edit.saving")
                  : t("partnerPages.edit.notifySave")}
              </button>
              <button
                onClick={() =>
                  updateDateMutation.mutate({
                    ...pendingPayload,
                    suppressEmailNotification: true,
                  })
                }
                disabled={updateDateMutation.isPending}
                className="btn-secondary w-full"
              >
                {t("partnerPages.edit.notifySkip")}
              </button>
              <button
                onClick={() => {
                  setPendingPayload(null);
                  updateDateMutation.reset();
                }}
                disabled={updateDateMutation.isPending}
                className="text-sm text-gray-500 hover:text-gray-700 w-full text-center mt-1"
              >
                {t("partnerPages.edit.cancel")}
              </button>
            </div>
          </div>
        </BookingDialog>
      )}
    </>
  );
}
